import React from 'react';

import Loadable from 'react-loadable';
import uuid from 'uuid/v4';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	CornerStyle,
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	RenderVariant,
	Height as Size,
} from '@atlassian/company-hub-common';

import {
	CARDS_EXTENSION_KEY,
	CARDS_EXTENSION_TYPE,
	CARDS_EXTENSION_VERSION_3,
} from '../../constants';
import {
	Alignment,
	type CardsExtensionParameters,
	CardsLayout,
	ImagePosition,
	Orientation,
} from '../../types';
import { type CardsExtensionProps } from '../cards-extension';

import { CARDS_IMAGES_V3, CARDS_IMAGES_V5 } from './constants';
import { messages } from './messages';

const CardsExtension = Loadable({
	loader: async () =>
		(await import(/* webpackChunkName: "@atlaskit-internal_CardsExtension" */ '../cards-extension'))
			.CardsExtension,
	loading: () => <UFOLoadHold name="cards-extension-loader"></UFOLoadHold>,
});

export const createCard = (imageSrc: string, imageAltText: string) => ({
	cardId: uuid(),
	link: '',
	isLinkUserEdited: false,
	title: '',
	isTitleUserEdited: false,
	description: '',
	isDescriptionUserEdited: false,
	imageSrc,
	imagePosition: ImagePosition.MIDDLE,
	imageAltText,
});

let cards: ReturnType<typeof createCard>[] | null = null;
export const getDefaultCards = () => {
	if (cards) {
		return cards;
	}

	const initialCards = fg('company_hub_cards_visual_update') ? CARDS_IMAGES_V5 : CARDS_IMAGES_V3;

	return (cards = initialCards.map(({ src, alt }) => createCard(src, alt)));
};

export const getCardsExtensionParameters = (
	extensionTitle: string,
	isAutoscrollGateEnabled: boolean,
	isCardsV1GateEnabled: boolean,
	isCardsV2GateEnabled: boolean,
	isDynamicCardsAIDescriptionsEnabled: boolean,
): CardsExtensionParameters => {
	const parameters: CardsExtensionParameters = {
		size: isCardsV1GateEnabled || isCardsV2GateEnabled ? Size.LARGE : Size.MEDIUM,
		cards: getDefaultCards(),
		isAvatarShown: true,
		isPublishDateShown: false,
		extensionTitle,
		version: CARDS_EXTENSION_VERSION_3,
		renderVariant: RenderVariant.MANUAL,
		isSegmentationShown: false,
		orientation:
			isCardsV1GateEnabled || isCardsV2GateEnabled ? Orientation.STACKED : Orientation.ROW,
		alignment: Alignment.LEFT,
	};

	if (isAutoscrollGateEnabled) {
		// default to false unless user already assigned autoscroll to enabled/disabled
		parameters.isAutoscrollEnabled ??= false;
	}

	if (isCardsV1GateEnabled) {
		parameters.isAvatarShown = false;
		parameters.backgroundColor ??= token('elevation.surface.overlay');
		parameters.textColor ??= token('color.text');
		parameters.cornerStyle ??= CornerStyle.ROUNDED;
		parameters.isSocialStatsShown ??= false;
		parameters.isIconShown ??= true;
	}

	if (isCardsV2GateEnabled) {
		parameters.layout ??= CardsLayout.FIXED_IMAGE_HEIGHT;
		parameters.isPaddingEnabled ??= true;
		parameters.isLabelShown ??= true;
		parameters.borderColor ??= DEFAULT_GRAY_COLOR;
	}

	if (isDynamicCardsAIDescriptionsEnabled) {
		parameters.isDescriptionShown = true;
	}

	return parameters;
};

export const cardsExtensionProvider = ({ contentId, propsProvider, mode }: CardsExtensionProps) => {
	return async ({
		editorActions,
		openConfigPanel,
		intl,
		setUpsellModalSource,
	}: CustomSitesExtensionProviderProps) => {
		const parameters = getCardsExtensionParameters(
			intl.formatMessage(messages.linkCardsMacroTitle),
			fg('company_hub_cards_autoscroll'),
			fg('company_hub_cards_visual_update'),
			fg('company_hub_cards_v2'),
			fg('company_hub_dynamic_cards_ai_descriptions'),
		);
		return new DefaultExtensionProvider([
			getExtensionManifest<CardsExtensionParameters>({
				title: intl.formatMessage(messages.linkCardsMacroTitle),
				type: CARDS_EXTENSION_TYPE,
				key: CARDS_EXTENSION_KEY,
				description: intl.formatMessage(messages.linkCardsMacroDescription),
				icons: {
					'48': () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_loadable-CardsExtensionIcon" */
							'../assets/cards-extension-icon'
						).then((mod) => mod.CardsExtensionIcon),
				},
				parameters,
				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (!!selectedLocalId && openConfigPanel) {
						openConfigPanel(selectedLocalId);
					}
				},
				render:
					async () =>
					({ node }: any) => {
						return (
							<CardsExtension
								extensionNode={node}
								contentId={contentId}
								propsProvider={propsProvider}
								mode={mode}
							/>
						);
					},
				quickInsertAction: async () =>
					Promise.resolve({
						type: 'extension',
						key: CARDS_EXTENSION_KEY,
						attrs: {
							extensionType: CARDS_EXTENSION_TYPE,
							extensionKey: CARDS_EXTENSION_KEY,
							layout: fg('company_hub_cards_visual_update') ? 'wide' : 'default',
							parameters,
						},
					}),
				source: 'premiumCardsInsert',
				setUpsellModalSource,
			}),
		]);
	};
};
